import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Search, Package, Tags, Layers, Wallet, Grid3x3 } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';
const GRACE_BRAND_ID = '29072fbc-294f-49f1-bb0b-6868f7e54d41';

const fmtInt = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtTL = (n) =>
  n == null ? '—' : `${Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`;
const fmtPct = (n, d = 1) =>
  `%${(Number(n || 0) * 100).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d })}`;

// Kategori/aralık barları için basit renk paleti (marka renklerinden bağımsız).
const BAR_COLORS = [
  '#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#14b8a6', '#f43f5e', '#84cc16', '#eab308',
];

function StatTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 text-gray-500">
        <Icon size={16} />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

// Yatay bar-list: { name, count, ...} dizisi + toplam
function BarList({ title, subtitle, rows, total, extra }) {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0) || 1;
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-1 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {subtitle && <span className="text-xs text-gray-400">{subtitle}</span>}
      </div>
      <div className="mt-3 space-y-2.5">
        {rows.map((r, i) => (
          <div key={r.name} className="group">
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="truncate pr-2 text-gray-700" title={r.name}>{r.name}</span>
              <span className="shrink-0 tabular-nums text-gray-500">
                {fmtInt(r.count)}
                {total ? <span className="ml-1 text-xs text-gray-400">({fmtPct(r.count / total, 0)})</span> : null}
                {extra ? <span className="ml-2 text-xs text-gray-400">{extra(r)}</span> : null}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${(r.count / max) * 100}%`, backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function GraceCatalog() {
  const { selectedBrand } = useAuth();
  const isGrace = selectedBrand?.id === GRACE_BRAND_ID;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');

  const fetchData = useCallback(async () => {
    if (!isGrace) return;
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE}/api/grace-catalog/overview`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (e) {
      setData(null);
      setError(e?.message || 'Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [isGrace, selectedBrand?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const products = data?.products || [];
  const filtered = useMemo(() => {
    let list = products;
    if (brandFilter) list = list.filter((p) => p.brand === brandFilter);
    if (catFilter) list = list.filter((p) => p.category === catFilter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(needle) || (p.sku || '').toLowerCase().includes(needle)
      );
    }
    return list;
  }, [products, q, brandFilter, catFilter]);

  // ── Gate ──────────────────────────────────────────────────────────────────
  if (!selectedBrand?.id) {
    return <div className="p-6 text-center text-gray-500">Lütfen bir marka seçin.</div>;
  }
  if (!isGrace) {
    return (
      <div className="p-6">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-10 text-center">
          <p className="text-amber-800">Bu sayfa yalnızca <strong>Grace Brands</strong> markası için kullanılabilir.</p>
        </div>
      </div>
    );
  }

  const t = data?.totals;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Grace Brands · Ürün Kataloğu</h1>
          <p className="mt-1 text-sm text-gray-500">
            gracebrands.com kataloğu · marka, ürün tipi ve fiyat aralığı dağılımı
            {data?.generatedAt && (
              <span className="ml-2 text-gray-400">
                · {new Date(data.generatedAt).toLocaleString('tr-TR')}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Yenile
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <RefreshCw size={20} className="mr-2 animate-spin" /> Katalog yükleniyor…
        </div>
      )}

      {t && (
        <>
          {/* KPI tiles */}
          <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-5">
            <StatTile icon={Package} label="Toplam Ürün" value={fmtInt(t.products)} sub={`${fmtInt(t.priced)} fiyatlı`} />
            <StatTile icon={Grid3x3} label="Ana Kategori" value={fmtInt(t.categories)} sub={`${fmtInt(t.productTypes)} ürün tipi`} />
            <StatTile icon={Tags} label="Marka / Tasarımcı" value={fmtInt(t.brands)} sub="markalı" />
            <StatTile icon={Layers} label="Ürün Tipi" value={fmtInt(t.productTypes)} sub="çeşit" />
            <StatTile
              icon={Wallet}
              label="Ortalama Fiyat"
              value={fmtTL(t.avgPrice)}
              sub={`medyan ${fmtTL(t.medianPrice)} · ${fmtTL(t.minPrice)}–${fmtTL(t.maxPrice)}`}
            />
          </div>

          {/* Ana kategori + Fiyat aralığı yan yana */}
          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BarList
              title="Ana Kategoriye Göre Dağılım"
              subtitle={`${fmtInt(t.categories)} kategori`}
              rows={data.categories || []}
              total={t.products}
              extra={(r) => (r.avgPrice ? `ort ${fmtTL(r.avgPrice)}` : '')}
            />
            <BarList
              title="Fiyat Aralığına Göre Ürün Sayısı"
              subtitle={`${fmtInt(t.priced)} fiyatlı ürün`}
              rows={(data.priceBuckets || []).map((b) => ({ name: b.label, count: b.count }))}
              total={t.priced}
            />
          </div>

          {/* Marka + Ürün tipi yan yana */}
          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BarList
              title="Marka / Tasarımcıya Göre Ürün Sayısı"
              subtitle={`${fmtInt(t.brands)} marka`}
              rows={data.brands || []}
              total={t.products}
              extra={(r) => (r.avgPrice ? `ort ${fmtTL(r.avgPrice)}` : '')}
            />
            <BarList
              title="Ürün Tipine Göre Dağılım"
              subtitle={`${fmtInt(t.productTypes)} tip`}
              rows={(data.productTypes || []).slice(0, 20)}
              total={t.products}
              extra={(r) => (r.avgPrice ? `ort ${fmtTL(r.avgPrice)}` : '')}
            />
          </div>

          {/* Ürün tablosu */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-900">
                Ürünler <span className="text-gray-400">({fmtInt(filtered.length)})</span>
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={catFilter}
                  onChange={(e) => setCatFilter(e.target.value)}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                >
                  <option value="">Tüm kategoriler</option>
                  {(data.categories || []).map((c) => (
                    <option key={c.name} value={c.name}>{c.name} ({c.count})</option>
                  ))}
                </select>
                <select
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                >
                  <option value="">Tüm markalar</option>
                  {(data.brands || []).map((b) => (
                    <option key={b.name} value={b.name}>{b.name} ({b.count})</option>
                  ))}
                </select>
                <div className="relative">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Ürün adı / SKU ara…"
                    className="w-56 rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700"
                  />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="py-2 pr-3 font-medium">Görsel</th>
                    <th className="py-2 pr-3 font-medium">Ürün</th>
                    <th className="py-2 pr-3 font-medium">Marka</th>
                    <th className="py-2 pr-3 font-medium">Kategori</th>
                    <th className="py-2 pr-3 font-medium">Tip</th>
                    <th className="py-2 pr-3 font-medium">SKU</th>
                    <th className="py-2 pr-3 text-right font-medium">Fiyat</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 300).map((p, i) => (
                    <tr key={p.sku || i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2 pr-3">
                        {p.image ? (
                          <img
                            src={p.image}
                            alt=""
                            loading="lazy"
                            className="h-10 w-10 rounded-lg border border-gray-100 object-cover"
                            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-gray-100" />
                        )}
                      </td>
                      <td className="py-2 pr-3 max-w-xs">
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noreferrer"
                          className="line-clamp-2 text-gray-800 hover:text-indigo-600 hover:underline"
                          title={p.name}
                        >
                          {p.name}
                        </a>
                      </td>
                      <td className="py-2 pr-3 text-gray-600">{p.brand}</td>
                      <td className="py-2 pr-3 text-gray-500">{p.category}</td>
                      <td className="py-2 pr-3 text-gray-500">{p.productType}</td>
                      <td className="py-2 pr-3 font-mono text-xs text-gray-400">{p.sku}</td>
                      <td className="py-2 pr-3 text-right font-medium tabular-nums text-gray-900">{fmtTL(p.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length > 300 && (
                <div className="mt-3 text-center text-xs text-gray-400">
                  İlk 300 ürün gösteriliyor · filtreyle daralt
                </div>
              )}
              {filtered.length === 0 && (
                <div className="py-10 text-center text-gray-400">Eşleşen ürün yok.</div>
              )}
            </div>
          </div>

          {/* Kampanya görselleri — çerçeveli "sepette %30 indirim" */}
          {data.promos?.items?.length > 0 && (
            <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="mb-1 flex items-baseline justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Kampanya Görselleri</h3>
                <span className="text-xs text-gray-400">Sepette %{data.promos.discount} indirim · çerçeveli</span>
              </div>
              <p className="mb-4 text-xs text-gray-400">
                Katalogdan seçilen ürünler için hazır sosyal/feed görseli. Yeniden üretmek için:
                <code className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">python3 scripts/generate_grace_promo.py</code>
              </p>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {data.promos.items.map((pr) => (
                  <figure key={pr.sku || pr.file} className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
                    <a href={pr.url} target="_blank" rel="noreferrer">
                      <img
                        src={`${API_BASE}${pr.image}`}
                        alt={pr.name}
                        loading="lazy"
                        className="w-full object-contain"
                      />
                    </a>
                    <figcaption className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                      <span className="truncate text-gray-600" title={pr.name}>{pr.name}</span>
                      <span className="shrink-0 font-medium text-red-600">
                        {fmtTL(pr.new)} <span className="ml-1 text-gray-400 line-through">{fmtTL(pr.old)}</span>
                      </span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          )}

          {/* Meta çerçeveleri — renkli kare çerçeve, sadece "sepette %30 indirim" */}
          {data.promosMeta?.sets?.length > 0 && (
            <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="mb-1 flex items-baseline justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Meta Reklam Çerçeveleri</h3>
                <span className="text-xs text-gray-400">1080×1080 · sadece indirim yazısı</span>
              </div>
              <p className="mb-4 text-xs text-gray-400">
                Ürünün çevresinde renkli çerçeve, tek mesaj. Yeniden üretmek için:
                <code className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">python3 scripts/generate_grace_meta_frame.py</code>
              </p>
              {data.promosMeta.sets.map((set) => (
                <div key={set.theme} className="mb-5 last:mb-0">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">{set.label}</span>
                    <span className="text-xs text-gray-400">· sepette %{set.discount} indirim</span>
                  </div>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {set.items.map((pr) => (
                      <figure key={pr.sku || pr.file} className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
                        <a href={pr.url} target="_blank" rel="noreferrer">
                          <img src={`${API_BASE}${pr.image}`} alt={pr.name} loading="lazy" className="w-full object-contain" />
                        </a>
                        <figcaption className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                          <span className="truncate text-gray-600" title={pr.name}>{pr.name}</span>
                          <span className="shrink-0 font-medium text-red-600">
                            {fmtTL(pr.new)} <span className="ml-1 text-gray-400 line-through">{fmtTL(pr.old)}</span>
                          </span>
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
